import { c as _c } from "react/compiler-runtime";
import { pinToPositionStyle } from "@osmix/ui-primitives";
import { useTranslation } from "react-i18next";
import type { Idea } from "@/api";
import { useIdeaPinProducts } from "@/api/hooks/use-ideas";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { formatPrice } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
type Props = {
  idea: Idea | null;
  onClose: () => void;
  // Provided in the moderation tab so the admin can act straight from the
  // detail view; omitted (view-only) elsewhere.
  onApprove?: (idea: Idea) => void;
  onReject?: (idea: Idea) => void;
};

// Full view of a seller idea: every picture at size with its shoppable pins
// overlaid, plus the products each pin points to (resolved by id). Pins are
// numbered globally so the badges on the image match the product list below.
export function IdeaDetailDialog(t0) {
  const $ = _c(26);
  const {
    idea,
    onClose,
    onApprove,
    onReject
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    data,
    isFetching: loading
  } = useIdeaPinProducts(idea);
  let t1;
  if ($[0] !== data) {
    t1 = data ?? {};
    $[0] = data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const products = t1;
  let T0;
  let T1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] !== idea || $[3] !== loading || $[4] !== onApprove || $[5] !== onClose || $[6] !== onReject || $[7] !== products || $[8] !== t) {
    const flatPins = idea ? idea.images.flatMap(_temp2) : [];
    const numberOf = pinId => flatPins.findIndex(p => p.id === pinId) + 1;
    T1 = Dialog;
    t4 = !!idea;
    if ($[15] !== onClose) {
      t5 = next => {
        if (!next) {
          onClose();
        }
      };
      $[15] = onClose;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    T0 = DialogContent;
    t2 = "max-h-[90vh] max-w-2xl overflow-y-auto";
    t3 = idea && <><DialogHeader className="pr-8"><div className="flex flex-wrap items-center gap-2"><DialogTitle>{idea.title}</DialogTitle><Badge variant="secondary">{t(`ideas.status.${idea.status}`)}</Badge></div></DialogHeader><div className="space-y-1 text-sm"><p className="font-medium text-foreground">{idea.shopName ?? idea.shopId}</p>{idea.description && <p className="text-muted-foreground">{idea.description}</p>}<p className="text-xs text-muted-foreground">{t("ideas.meta", {
            images: idea.images.length,
            products: idea.productCount
          })}</p></div>{idea.status === "rejected" && idea.rejectionReason && <p className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">{idea.rejectionReason}</p>}<div className="space-y-3">{idea.images.map(img_0 => <div key={img_0.id} className="flex justify-center"><div className="relative inline-block overflow-hidden rounded-lg bg-muted"><img src={img_0.url} alt="" className="max-h-[360px] w-auto max-w-full object-contain" />{img_0.pins.map(pin_0 => <span key={pin_0.id} style={pinToPositionStyle(pin_0)} className="absolute flex size-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-primary text-xs font-bold text-primary-foreground shadow">{numberOf(pin_0.id)}</span>)}</div></div>)}</div><div className="space-y-2"><h3 className="text-sm font-semibold">{t("ideas.detail.taggedProducts")}</h3>{flatPins.length === 0 ? <p className="text-sm text-muted-foreground">{t("ideas.detail.noProducts")}</p> : <ul className="space-y-2">{flatPins.map((pin_1, index) => {
            const product = products[pin_1.productId];
            return <li key={pin_1.id} className="flex items-center gap-3 rounded-lg border p-2"><span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{index + 1}</span><span className="size-12 shrink-0 overflow-hidden rounded bg-muted">{product?.photos[0] && <img src={product.photos[0]} alt="" className="size-full object-cover" />}</span><div className="min-w-0 flex-1"><p className="line-clamp-1 text-sm font-medium">{product?.name ?? (loading ? t("ideas.loading") : pin_1.productId)}</p>{product && <p className="text-xs text-muted-foreground">{product.brand} · {formatPrice(product.price)}</p>}</div>{product && <Badge variant="outline">{t(`ideas.status.${product.status}`, {
                  defaultValue: product.status
                })}</Badge>}</li>;
          })}</ul>}</div>{(onApprove || onReject) && <div className="flex justify-end gap-2 border-t pt-4">{onReject && <Button variant="outline" onClick={() => onReject(idea)} disabled={idea.status === "rejected"}>{t("ideas.actions.reject")}</Button>}{onApprove && <Button onClick={() => onApprove(idea)} disabled={idea.status === "published"}>{t("ideas.actions.approve")}</Button>}</div>}</>;
    $[2] = idea;
    $[3] = loading;
    $[4] = onApprove;
    $[5] = onClose;
    $[6] = onReject;
    $[7] = products;
    $[8] = t;
    $[9] = T0;
    $[10] = T1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    T0 = $[9];
    T1 = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
  }
  let t6;
  if ($[17] !== T0 || $[18] !== t2 || $[19] !== t3) {
    t6 = <T0 className={t2}>{t3}</T0>;
    $[17] = T0;
    $[18] = t2;
    $[19] = t3;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== T1 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <T1 open={t4} onOpenChange={t5}>{t6}</T1>;
    $[21] = T1;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}
function _temp2(img) {
  return img.pins.map(_temp);
}
function _temp(pin) {
  return pin;
}